/**
 * useConfirm — Promise-based MUI confirmation dialog.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ message: 'Delete this item?', severity: 'error' });
 *   if (!ok) return;
 *   // ... proceed
 *
 * Add <ConfirmProvider> once in App (wrapping the whole tree).
 */
import React, { createContext, useContext, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export interface ConfirmOptions {
  /** Main question shown in the dialog body */
  message: string;
  /** Optional title — defaults to a severity-appropriate label */
  title?: string;
  /** Controls header colour and icon. Default: 'warning' */
  severity?: 'error' | 'warning' | 'info';
  /** Label for the confirm button. Default: 'Confirm' */
  confirmText?: string;
  /** Label for the cancel button. Default: 'Cancel' */
  cancelText?: string;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(() => Promise.resolve(false));

interface State extends ConfirmOptions {
  open: boolean;
}

const SEVERITY_STYLES: Record<
  NonNullable<ConfirmOptions['severity']>,
  { gradient: string; icon: React.ReactNode }
> = {
  error: {
    gradient: 'linear-gradient(135deg,#e53e3e 0%,#c53030 100%)',
    icon: <DeleteOutlineIcon sx={{ fontSize: 26 }} />,
  },
  warning: {
    gradient: 'linear-gradient(135deg,#ed8936 0%,#c05621 100%)',
    icon: <WarningAmberIcon sx={{ fontSize: 26 }} />,
  },
  info: {
    gradient: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
    icon: <InfoOutlinedIcon sx={{ fontSize: 26 }} />,
  },
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const resolveRef = useRef<((v: boolean) => void) | undefined>(undefined);
  const [state, setState] = useState<State>({
    open: false,
    message: '',
    severity: 'warning',
  });

  const confirm: ConfirmFn = (options) =>
    new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, ...options });
    });

  const handleClose = (result: boolean) => {
    setState((prev) => ({ ...prev, open: false }));
    resolveRef.current?.(result);
  };

  const severity = state.severity ?? 'warning';
  const style = SEVERITY_STYLES[severity];

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={state.open} onClose={() => handleClose(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            background: style.gradient,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2,
          }}
        >
          {style.icon}
          <Typography variant="h6" component="span" fontWeight={700}>
            {state.title ?? (severity === 'error' ? 'Confirm Delete' : 'Confirm')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Typography variant="body1">{state.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={() => handleClose(false)}>
            {state.cancelText ?? 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color={severity === 'error' ? 'error' : severity === 'info' ? 'primary' : 'warning'}
            onClick={() => handleClose(true)}
            autoFocus
          >
            {state.confirmText ?? 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => useContext(ConfirmContext);
